import React, { useState } from 'react';
import { Button, Spinner } from '@telus-uds/components-web';
import PropTypes from 'prop-types';

import * as Yup from 'yup';
import { useFormik } from 'formik';
import request from '../../../../utils/request';
import Modal from '../../../../design/shared/modal';
import { Spacer, ActionsContainer, SpinnerContainer } from '../css';
import { FieldCheckbox, FieldInput, FieldMultiLineText } from '../../../fields';
import { Error } from '../../../../design/elements';
import FieldFile from '../../../fieldFile';
import { calculateFinancials, extractNumber, getTcvDmTermThreshold } from '../../../fmUtils/utils';
import { CONSTANT } from '../../../../utils/constant';

const AddDiscountPopup = ({ isModalOpen, setModalOpen, orderId, term, onDiscountAdded, onDiscountError, onboardingFee, managedItServices, discountToOveride, onDiscountUpdated, channel }) => {
  const [isLoading, setIsLoading] = useState(false);
  const userProfile = JSON.parse(localStorage.getItem('userProfile'));
  const validationSchema = Yup.object().shape({
    monthlyDiscount: Yup.number()
      .min(0,'Monthly Discount must be 0 or a positive number')
      .nullable()
      .test(
        'is-less-than-max',
        `Monthly Discount must be less than $${managedItServices.toFixed(2)}`,
        (value) => value ? (value < managedItServices) : true
      ),
  
    oneTimeDiscount: Yup.number()
      .min(0,'One Time Discount must be 0 or a positive number')
      .nullable()
      .test(
        'is-less-than-or-equal-max',
        `One Time Discount must be less than or equal to $${onboardingFee.toFixed(2)}`,
        (value) => {
          if (!value) return true;
          // Round both values to 2 decimal places to avoid floating-point precision issues
          const roundedValue = Math.round(value * 100) / 100;
          const roundedMax = Math.round(onboardingFee * 100) / 100;
          return roundedValue <= roundedMax;
        }
      ),
  
    overrideMonthly: Yup.number()
      .positive('Reduce Monthly Discount must be a positive number')
      .nullable()
      .when('discountToOveride', {
        is: () => discountToOveride !== undefined, // Apply validation only if discountToOveride is defined
        then: (schema) =>
          schema.when(['monthlyDiscount'], (monthlyDiscount, schema) => 
            schema.test(
              'is-less-than-monthly',
              'Reduce Monthly Discount must be less than Monthly Discount',
              (value) => value < monthlyDiscount
            ))
      }),
  
    overrideOnetime: Yup.number()
      .positive('Reduce One Time Discount must be a positive number')
      .nullable()
      .when('discountToOveride', {
        is: () => discountToOveride !== undefined, // Apply validation only if discountToOveride is defined
        then: (schema) =>
          schema.when(['oneTimeDiscount'], (oneTimeDiscount, schema) => 
            schema.test(
              'is-less-than-onetime',
              'Reduce One Time Discount must be less than One Time Discount',
              (value) => value < oneTimeDiscount
            )
          )
      }),
  
    discountRationale: Yup.string()
      .when('discountToOveride', {
        is: () => discountToOveride === undefined,
        then: (schema) => schema.required('Discount rationale is required'),
        otherwise: (schema) => schema.notRequired(),
      }),

    pricingConfirmation: Yup.boolean()
      .when('discountToOveride', {
        is: () => discountToOveride === undefined,
        then: (schema) => schema.oneOf([true], 'Pricing Confirmation is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
  // eslint-disable-next-line func-names
  }).test('at-least-one', 'Either Monthly Discount or One Time Discount is required', function(values) {
    const { monthlyDiscount, oneTimeDiscount } = values;
    if (!monthlyDiscount && !oneTimeDiscount) {
      // eslint-disable-next-line react/no-this-in-sfc
      return this.createError({ path: 'monthlyDiscount', message: 'Either Monthly Discount or One Time Discount is required' });
    }
    return true;
  // eslint-disable-next-line func-names
  }).test('at-least-one-override', 'Either Reduce Monthly Discount or Reduce One Time Discount is required', function(values) {
    const { overrideMonthly, overrideOnetime } = values;
    if (discountToOveride && !overrideMonthly && !overrideOnetime) {
      // eslint-disable-next-line react/no-this-in-sfc
      return this.createError({ path: 'overrideMonthly', message: 'Either Reduce Monthly Discount or Reduce One Time Discount is required' });
    }
    return true;
  });

  const uploadFile = (file, id) => {
    const formData = new FormData();
    formData.append('file', file);
    return request.post(`/discounts/${id}/attachment`, formData);
  };

  const getApprovalStatus = (isTCVDMGreaterThanThirty, isTerm12MonthsWithLessThanThreeThousand) => {
    if(channel === CONSTANT.KEYS.CHANNELS['Customer Success Fully Managed deVries']) {
      return isTCVDMGreaterThanThirty ? 'Auto-Approved' : 'Pending-Approval';
    }
    return (isTCVDMGreaterThanThirty && !isTerm12MonthsWithLessThanThreeThousand) ? 'Auto-Approved' : 'Pending-Approval';
  };
  
  const formik = useFormik({
    enableReinitialize: true,  
    initialValues: {
      monthlyDiscount: discountToOveride ? discountToOveride.approvedMonthly :  '',
      oneTimeDiscount: discountToOveride ? discountToOveride.approvedOnetime :  '',
      overrideMonthly: discountToOveride ? discountToOveride.overrideMonthly : '',
      overrideOnetime: discountToOveride ? discountToOveride.overrideOnetime : '',
      discountRationale: '',
      dicountApprovalId: discountToOveride ? discountToOveride.id :  '',
      attachment: '',
      pricingConfirmation: false
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        if(discountToOveride) {
          await request.put(`/discounts/${discountToOveride.id}`, {
            overrideMonthly: values.overrideMonthly || 0,
            overrideOnetime: values.overrideOnetime || 0
          });
          onDiscountUpdated();
        }
        else {
          const isTCVDMGreaterThanThirty = calculateFinancials(extractNumber(term), managedItServices, onboardingFee, values.monthlyDiscount, values.oneTimeDiscount,null,null,null,channel).TCV_DM_Percentage > getTcvDmTermThreshold(term, channel);
          const isTerm12MonthsWithLessThanThreeThousand = ((term === '12 months') && (managedItServices < 3000));
          const res = await request.post('/discounts', {
                orderId,
                userId: userProfile.id,
                term,
                monthly: values.monthlyDiscount || 0,
                onetime: values.oneTimeDiscount || 0,
                approvedMonthly: isTCVDMGreaterThanThirty ? values.monthlyDiscount : undefined,
                approvedOnetime: isTCVDMGreaterThanThirty ? values.oneTimeDiscount : undefined,
                comment: values.discountRationale,
                status: getApprovalStatus(isTCVDMGreaterThanThirty, isTerm12MonthsWithLessThanThreeThousand),
                requestedForOnboardingFeeValue: onboardingFee,
                requestedForManagedITServicesValue: managedItServices
              });
          if(values.attachment) await uploadFile(values.attachment, res.data.id);
          onDiscountAdded(res.data.id);
        }
        setIsLoading(false);
        setModalOpen(false);
        formik.resetForm(); 
      } catch (e) {
        onDiscountError();
        setIsLoading(false);
        setModalOpen(false);
      }
    }
  });

  return (
    <Modal
      open={isModalOpen}
      setOpen={() => setModalOpen(false)}
      size="large"
      header={discountToOveride ? "Reduce Discount" : "Add New Discount"}
    >
      {isLoading ? (
          <SpinnerContainer>
        <Spinner show label="Adding Discount..." />

          </SpinnerContainer>
      ) : (
        <form>
          {
            discountToOveride &&
            <div>
            <FieldInput
              data={{
                name: 'dicountApprovalId',
                type: 'number',
                label: 'Discount Approval ID',
                value: formik.values.dicountApprovalId,
                onChange: formik.handleChange,
                disabled: discountToOveride
              }}
            />
            {formik.touched.dicountApprovalId &&
              formik.errors.dicountApprovalId && (
                <Error text={formik.errors.dicountApprovalId} />
              )}
          </div>
          }
          <div>
            <FieldInput
              data={{
                name: 'monthlyDiscount',
                type: 'number',
                label: 'Monthly Discount ($)',
                value: formik.values.monthlyDiscount,
                onChange: formik.handleChange,
                disabled: discountToOveride
              }}
            />
            {formik.touched.monthlyDiscount &&
              formik.errors.monthlyDiscount && (
                <Error text={formik.errors.monthlyDiscount} />
              )}
          </div>
          <div>
            <FieldInput
              data={{
                name: 'oneTimeDiscount',
                type: 'number',
                label: 'One Time Discount ($)',
                value: formik.values.oneTimeDiscount,
                onChange: formik.handleChange,
                disabled: discountToOveride
              }}
            />
            {formik.touched.oneTimeDiscount &&
              formik.errors.oneTimeDiscount && (
                <Error text={formik.errors.oneTimeDiscount} />
              )}
          </div>
          {
            !discountToOveride &&
            <>
              <div>
                <FieldMultiLineText
                  data={{
                    name: 'discountRationale',
                    type: 'text',
                    required: true,
                    label:
                      'Please provide any supporting business rationale for your request',
                    value: formik.values.discountRationale,
                    onChange: (e) =>
                      formik.setFieldValue('discountRationale', e.target.value)
                  }}
                />
                {formik.touched.discountRationale &&
                  formik.errors.discountRationale && (
                    <Error text={formik.errors.discountRationale} />
                  )}
              </div>
              <div>
                <FieldFile
                  data={{
                    name: 'attachment',
                    label: 'Attachment',
                    value: formik.values.attachment,
                    onChange: (event) => {
                      const file = event.currentTarget.files[0]; // Get the selected file
                      formik.setFieldValue('attachment', file); // Update Formik state
                    }
                  }}
                />
              </div>
              <div>
                <FieldCheckbox
                  data={{
                    name: 'pricingConfirmation',
                    required: true,
                    label:
                      'I confirm the original pricing has been presented to the customer before requesting a discount.*',
                    checked: formik.values.pricingConfirmation,
                    value: formik.values.pricingConfirmation,
                    onChange: formik.handleChange
                  }}
                />
                {formik.touched.pricingConfirmation &&
                  formik.errors.pricingConfirmation && (
                    <Error text={formik.errors.pricingConfirmation} />
                  )
                }
              </div>
            </>
          }
          {
            discountToOveride &&
            <>
            <div>
                <FieldInput
                  data={{
                    name: 'overrideMonthly',
                    type: 'Number',
                    label: 'Reduce Monthly Discount ($)',
                    value: formik.values.overrideMonthly,
                    onChange: formik.handleChange
                  }}
                />
                {formik.touched.overrideMonthly &&
                  formik.errors.overrideMonthly && (
                    <Error text={formik.errors.overrideMonthly} />
                  )}
              </div>     
              <div>
                <FieldInput
                  data={{
                    name: 'overrideOnetime',
                    type: 'Number',
                    label: 'Reduce One Time Discount ($)',
                    value: formik.values.overrideOnetime,
                    onChange: formik.handleChange
                  }}
                />
                {
                  formik.touched.overrideOnetime &&
                  formik.errors.overrideOnetime && (
                    <Error text={formik.errors.overrideOnetime} />
                  )}
              </div>          
            </>
          }
          <Spacer />
          <ActionsContainer>
            <Button onClick={formik.handleSubmit}>
              {discountToOveride ? 'Reduce Discount' : 'Add Discount'}
            </Button>
          </ActionsContainer>
        </form>
      )}
    </Modal>
  );
};

AddDiscountPopup.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  setModalOpen: PropTypes.func.isRequired,
  orderId: PropTypes.number.isRequired,
  term: PropTypes.string.isRequired,
  onDiscountAdded: PropTypes.func.isRequired,
  onDiscountError: PropTypes.func.isRequired,
  onboardingFee: PropTypes.number.isRequired,
  managedItServices: PropTypes.number.isRequired,
  onDiscountUpdated: PropTypes.func.isRequired,
  channel: PropTypes.string.isRequired
};

export default AddDiscountPopup;
